import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Tooltip
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  Lock as LockIcon,
  ExpandMore as ExpandMoreIcon,
  Campaign as CampaignIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  WhatsApp as WhatsAppIcon,
  Google as GoogleIcon,
  Star as ReviewIcon,
  Share as ReferralIcon
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';
import { apiClient } from '../../services/api';
import WhatsAppApiConfigCard from './WhatsAppApiConfigCard';

interface MarketingSettingsData {
  permissions: Record<string, boolean>;
  channels: Record<string, any>;
}

interface MarketingSettingsProps {
  salonId: string;
}

const MarketingSettings: React.FC<MarketingSettingsProps> = ({ salonId }) => {
  const [settings, setSettings] = useState<MarketingSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { checkAccess, getRestrictions } = useSettings();
  
  const canWrite = checkAccess('marketing', 'write');
  const restrictions = getRestrictions('marketing');

  const marketingChannels = {
    'Email Marketing': {
      key: 'emailMarketing',
      icon: <EmailIcon />,
      description: 'Send promotional emails and newsletters',
      permissions: ['email_campaigns', 'email_automation', 'email_templates']
    },
    'SMS Marketing': {
      key: 'smsMarketing',
      icon: <SmsIcon />,
      description: 'Send SMS campaigns and reminders',
      permissions: ['sms_campaigns', 'sms_automation', 'sms_bulk']
    },
    'WhatsApp Business': {
      key: 'whatsappBusiness',
      icon: <WhatsAppIcon />,
      description: 'WhatsApp Business API integration',
      permissions: ['whatsapp_campaigns', 'whatsapp_automation']
    },
    'Social Media': {
      key: 'socialMedia',
      icon: <FacebookIcon />,
      description: 'Facebook and Instagram integration',
      permissions: ['facebook_posts', 'instagram_posts', 'social_automation']
    },
    'Google My Business': {
      key: 'googleBusiness',
      icon: <GoogleIcon />,
      description: 'Google My Business profile management',
      permissions: ['google_posts', 'google_reviews', 'google_analytics']
    },
    'Review Management': {
      key: 'reviewManagement',
      icon: <ReviewIcon />,
      description: 'Manage customer reviews and ratings',
      permissions: ['review_automation', 'review_responses', 'review_analytics']
    },
    'Referral Programs': {
      key: 'referralPrograms',
      icon: <ReferralIcon />,
      description: 'Customer referral and reward programs',
      permissions: ['referral_tracking', 'referral_rewards', 'referral_analytics']
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [salonId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/settings/${salonId}/marketing`);
      
      if (response.success) {
        setSettings(response.data.settings);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load marketing settings');
    } finally {
      setLoading(false);
    }
  };

  const isChannelRestricted = (channelKey: string): boolean => {
    const restrictedChannels = [
      'whatsappBusiness',
      'socialMedia',
      'googleBusiness',
      'reviewManagement',
      'referralPrograms'
    ];

    return restrictions.includes('no_social_media') && restrictedChannels.includes(channelKey);
  };

  const getChannelStatus = (channelKey: string) => {
    if (!settings) return { enabled: false, restricted: false };
    
    const enabled = settings.permissions[channelKey] || false;
    const restricted = isChannelRestricted(channelKey);
    
    return { enabled, restricted };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Alert severity="error">
        Failed to load marketing settings. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Marketing & Communication</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {restrictions.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {restrictions.map(restriction => (
                <Chip
                  key={restriction}
                  icon={<LockIcon />}
                  label={restriction.replace(/_/g, ' ')}
                  color="warning"
                  size="small"
                />
              ))}
            </Box>
          )}
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchSettings}
            disabled={loading}
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {restrictions.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Marketing Restrictions Active
          </Typography>
          <Typography variant="body2">
            Some marketing channels are restricted based on your salon type and governance settings.
            Contact Super Admin to request access to additional marketing features.
          </Typography>
        </Alert>
      )}

      {/* Phase 2: WhatsApp Business Cloud API credentials */}
      <WhatsAppApiConfigCard salonId={salonId} canWrite={canWrite} />

      <Grid container spacing={3}>
        {Object.entries(marketingChannels).map(([channelName, channelInfo]) => {
          const { enabled, restricted } = getChannelStatus(channelInfo.key);
          
          return (
            <Grid item xs={12} key={channelInfo.key}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {channelInfo.icon}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6">
                        {channelName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {channelInfo.description}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {restricted && (
                        <Tooltip title="This channel is restricted by governance rules">
                          <Chip
                            icon={<LockIcon />}
                            label="Restricted"
                            color="error"
                            size="small"
                          />
                        </Tooltip>
                      )}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={enabled}
                            disabled={!canWrite || restricted}
                          />
                        }
                        label={enabled ? 'Enabled' : 'Disabled'}
                      />
                    </Box>
                  </Box>

                  {enabled && !restricted && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          Channel Configuration
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {channelInfo.permissions.map(permission => (
                            <Grid item xs={12} sm={6} key={permission}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={settings.permissions[permission] || false}
                                    disabled={!canWrite}
                                    size="small"
                                  />
                                }
                                label={permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              />
                            </Grid>
                          ))}
                        </Grid>

                        {/* Channel-specific configuration */}
                        {channelInfo.key === 'emailMarketing' && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Email Configuration
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="From Name"
                                  value={settings.channels.email?.fromName || ''}
                                  disabled={!canWrite}
                                  size="small"
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="Reply-To Email"
                                  value={settings.channels.email?.replyTo || ''}
                                  disabled={!canWrite}
                                  size="small"
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        )}

                        {channelInfo.key === 'smsMarketing' && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              SMS Configuration
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="Sender ID"
                                  value={settings.channels.sms?.senderId || ''}
                                  disabled={!canWrite}
                                  size="small"
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  fullWidth
                                  label="Template ID"
                                  value={settings.channels.sms?.templateId || ''}
                                  disabled={!canWrite}
                                  size="small"
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {restricted && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      This marketing channel is not available for your salon type. 
                      Contact Super Admin to discuss upgrading your marketing permissions.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Marketing Analytics */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Marketing Analytics
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="primary">
                      0
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Campaigns
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="success.main">
                      0%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Email Open Rate
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="info.main">
                      0%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      SMS Delivery Rate
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="warning.main">
                      ₹0
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Marketing ROI
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MarketingSettings;